import { useState, useCallback } from 'react'
import apiService from '../../../services/ApiService'

const PAGE_SIZE = 12

const DEFAULT_FILTERS = {
  source: [],
  countries: [],
  taxType: [],
  documentType: [],
  dateFrom: '',
  dateTo: '',
  search: '',
  sortBy: 'publishDate',
  sortDir: 'desc',
  nullDates: false,
  seenStatus: 'NEW', // NEW | REVIEWED | ALL
}

const normalizePageResponse = (response) => {
  return response?.content
    ? { items: response.content, totalPages: response.totalPages ?? 1, totalItems: response.totalElements ?? 0 }
    : Array.isArray(response)
    ? { items: response, totalPages: Math.max(1, Math.ceil(response.length / PAGE_SIZE)), totalItems: response.length }
    : { items: [], totalPages: 1, totalItems: 0 }
}

export const useDocumentsManagement = (initialPageSize = PAGE_SIZE) => {
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [counts, setCounts] = useState({ newCount: 0, reviewedCount: 0, totalCount: 0 })

  const buildParams = useCallback(
    (page, overrideFilters, overridePageSize) => {
      const f = overrideFilters ?? filters
      const params = {
        page: page - 1,
        size: overridePageSize ?? pageSize,
      }
      if (f.source?.length) params.source = f.source
      if (f.countries?.length) params.countries = f.countries
      if (f.taxType?.length) params.taxType = f.taxType
      if (f.documentType?.length) params.documentType = f.documentType
      if (f.search?.trim()) params.search = f.search.trim()
      if (f.sortBy) params.sortBy = f.sortBy
      if (f.sortDir) params.sortDir = f.sortDir
      if (f.dateFrom) params.dateFrom = f.dateFrom
      if (f.dateTo) params.dateTo = f.dateTo
      if (f.nullDates) params.nullDates = true
      if (f.seenStatus && f.seenStatus !== 'ALL') params.seenStatus = f.seenStatus
      return params
    },
    [filters, pageSize],
  )

  const buildCountsParams = useCallback(
    (overrideFilters) => {
      const f = overrideFilters ?? filters
      const params = {}
      if (f.source?.length) params.source = f.source
      if (f.countries?.length) params.countries = f.countries
      if (f.taxType?.length) params.taxType = f.taxType
      if (f.documentType?.length) params.documentType = f.documentType
      if (f.search?.trim()) params.search = f.search.trim()
      if (f.dateFrom) params.dateFrom = f.dateFrom
      if (f.dateTo) params.dateTo = f.dateTo
      if (f.nullDates) params.nullDates = true
      return params
    },
    [filters],
  )

  const loadCounts = useCallback(
    async (overrideFilters) => {
      try {
        const params = buildCountsParams(overrideFilters)
        const res = await apiService.getAdminDocumentsCounts(params)
        setCounts({
          newCount: res?.newCount ?? 0,
          reviewedCount: res?.reviewedCount ?? 0,
          totalCount: res?.totalCount ?? ((res?.newCount ?? 0) + (res?.reviewedCount ?? 0)),
        })
      } catch (error) {
        // non-fatal — counts are an enhancement
        console.error('Failed to load document counts:', error)
      }
    },
    [buildCountsParams],
  )

  const loadDocuments = useCallback(
    async (page = currentPage, overrideFilters, overridePageSize) => {
      try {
        setLoading(true)
        const params = buildParams(page, overrideFilters, overridePageSize)
        const response = await apiService.getAdminDocuments(params)
        const activeFilters = overrideFilters ?? filters
        const { items, totalPages: tp, totalItems: ti } = normalizePageResponse(response)
        setDocuments(items)
        setTotalPages(tp)
        setTotalItems(ti)
        loadCounts(activeFilters)
      } catch (error) {
        console.error('Failed to load documents:', error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [currentPage, buildParams, filters, loadCounts],
  )

  const handleFilterChange = useCallback(
    (key, value) => {
      const newFilters = { ...filters, [key]: value }
      setFilters(newFilters)
      setCurrentPage(1)
      loadDocuments(1, newFilters)
    },
    [filters, loadDocuments],
  )

  const handleFiltersChange = useCallback(
    (partialFilters) => {
      const newFilters = { ...filters, ...partialFilters }
      setFilters(newFilters)
      setCurrentPage(1)
      loadDocuments(1, newFilters)
    },
    [filters, loadDocuments],
  )

  const handleSearch = useCallback(
    (search) => {
      handleFilterChange('search', search)
    },
    [handleFilterChange],
  )

  const handleSortChange = useCallback(
    (sortBy, sortDir) => {
      const newFilters = { ...filters, sortBy, sortDir }
      setFilters(newFilters)
      setCurrentPage(1)
      loadDocuments(1, newFilters)
    },
    [filters, loadDocuments],
  )

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page)
      loadDocuments(page)
    },
    [loadDocuments],
  )

  const handlePageSizeChange = useCallback(
    (newSize) => {
      setPageSize(newSize)
      setCurrentPage(1)
      loadDocuments(1, undefined, newSize)
    },
    [loadDocuments],
  )

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS)
    setCurrentPage(1)
    loadDocuments(1, DEFAULT_FILTERS)
  }, [loadDocuments])

  const handleSeenStatusChange = useCallback(
    (seenStatus) => {
      const newFilters = { ...filters, seenStatus }
      setFilters(newFilters)
      setCurrentPage(1)
      loadDocuments(1, newFilters)
    },
    [filters, loadDocuments],
  )

  const docKey = (d) => d._id ?? d.id

  const getVisibleTotalDelta = (seenStatus, nextSeen, changedCount) => {
    if (!changedCount || seenStatus === 'ALL') return 0
    if (seenStatus === 'NEW') return nextSeen ? -changedCount : changedCount
    if (seenStatus === 'REVIEWED') return nextSeen ? changedCount : -changedCount
    return 0
  }

  // Optimistic single mark — updates local state immediately, rolls back on failure.
  const markDocumentSeen = useCallback(
    async (documentId, seen) => {
      const prevDocs = documents
      const prevCounts = counts
      const prevTotalItems = totalItems
      const target = documents.find((d) => docKey(d) === documentId)
      const currentSeen = target ? target.seenStatus === 'REVIEWED' : !seen
      const changedCount = target ? (currentSeen !== seen ? 1 : 0) : 1
      // Optimistic update in current list
      setDocuments((cur) => {
        if (filters.seenStatus !== 'ALL' && changedCount > 0) {
          // when filtering by status, the doc leaves the visible list
          return cur.filter((d) => docKey(d) !== documentId)
        }
        return cur.map((d) => (docKey(d) === documentId ? { ...d, seenStatus: seen ? 'REVIEWED' : 'NEW' } : d))
      })
      setTotalItems((cur) => Math.max(0, cur + getVisibleTotalDelta(filters.seenStatus, seen, changedCount)))
      setCounts((c) => ({
        ...c,
        newCount: Math.max(0, c.newCount + (seen ? -changedCount : changedCount)),
        reviewedCount: Math.max(0, c.reviewedCount + (seen ? changedCount : -changedCount)),
      }))
      try {
        await apiService.updateDocumentSeenStatus(documentId, seen)
      } catch (error) {
        // rollback
        setDocuments(prevDocs)
        setCounts(prevCounts)
        setTotalItems(prevTotalItems)
        throw error
      }
    },
    [documents, counts, filters.seenStatus, totalItems],
  )

  const bulkMarkDocumentsSeen = useCallback(
    async (documentIds, seen) => {
      if (!documentIds?.length) return
      const idSet = new Set(documentIds)
      const prevDocs = documents
      const prevCounts = counts
      const prevTotalItems = totalItems
      const docsById = new Map(documents.map((d) => [docKey(d), d]))
      const changedIds = documentIds.filter((id) => {
        const doc = docsById.get(id)
        return doc ? (doc.seenStatus === 'REVIEWED') !== seen : true
      })
      const changedIdSet = new Set(changedIds)
      const changedCount = changedIds.length
      setDocuments((cur) => {
        if (filters.seenStatus !== 'ALL' && changedCount > 0) {
          return cur.filter((d) => !changedIdSet.has(docKey(d)))
        }
        return cur.map((d) => (idSet.has(docKey(d)) ? { ...d, seenStatus: seen ? 'REVIEWED' : 'NEW' } : d))
      })
      setTotalItems((cur) => Math.max(0, cur + getVisibleTotalDelta(filters.seenStatus, seen, changedCount)))
      setCounts((c) => ({
        ...c,
        newCount: Math.max(0, c.newCount + (seen ? -changedCount : changedCount)),
        reviewedCount: Math.max(0, c.reviewedCount + (seen ? changedCount : -changedCount)),
      }))
      try {
        await apiService.bulkUpdateDocumentSeenStatus(documentIds, seen)
      } catch (error) {
        setDocuments(prevDocs)
        setCounts(prevCounts)
        setTotalItems(prevTotalItems)
        throw error
      }
    },
    [documents, counts, filters.seenStatus, totalItems],
  )

  return {
    documents,
    loading,
    currentPage,
    totalPages,
    totalItems,
    filters,
    pageSize,
    counts,
    loadDocuments,
    handleFilterChange,
    handleFiltersChange,
    handleSearch,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange,
    resetFilters,
    handleSeenStatusChange,
    markDocumentSeen,
    bulkMarkDocumentsSeen,
  }
}
